import { useCallback, useEffect, useRef, useState } from "react";

export interface RelRecord {
    id: number;
    name: string;
}

export type RelSearch = (query: string, limit: number) => Promise<RelRecord[]>;

export interface RelState {
    query: string;
    open: boolean;
    loading: boolean;
    results: RelRecord[];
    active: number;
    hasMore: boolean;
}

const INITIAL: RelState = {
    query: "", open: false, loading: false, results: [], active: -1, hasMore: false,
};

export function useRelSearch(search: RelSearch, limit = 8, cacheKey = "") {
    const [state, setState] = useState<RelState>(INITIAL);
    const stateRef = useRef(state);
    stateRef.current = state;
    const seq = useRef(0);
    const searchTimer = useRef<number>();
    const closeTimer = useRef<number>();
    const lastKey = useRef<string | null>(null);
    const searchRef = useRef(search);
    searchRef.current = search;

    useEffect(() => () => {
        window.clearTimeout(searchTimer.current);
        window.clearTimeout(closeTimer.current);
    }, []);

    const patch = useCallback((values: Partial<RelState>) => {
        setState((current) => ({ ...current, ...values }));
    }, []);

    const run = useCallback(async ({ force = false, query }: { force?: boolean; query?: string } = {}) => {
        const term = (query ?? stateRef.current.query).trim();
        const key = JSON.stringify([cacheKey, term]);
        if (!force && key === lastKey.current && stateRef.current.results.length) {
            return;
        }
        const mine = ++seq.current;
        patch({ loading: true });
        try {
            const rows = await searchRef.current(term, limit + 1);
            if (mine !== seq.current) {
                return;
            }
            const results = rows.slice(0, limit);
            patch({ hasMore: rows.length > limit, results, active: results.length ? 0 : -1 });
            lastKey.current = key;
        } catch {
            if (mine === seq.current) {
                patch({ results: [], hasMore: false, active: -1 });
            }
        } finally {
            if (mine === seq.current) {
                patch({ loading: false });
            }
        }
    }, [cacheKey, limit, patch]);

    const openList = useCallback(({ refresh = false, query }: { refresh?: boolean; query?: string } = {}) => {
        window.clearTimeout(closeTimer.current);
        patch({ open: true });
        void run({ force: refresh, query });
    }, [patch, run]);

    const close = useCallback(() => {
        window.clearTimeout(searchTimer.current);
        window.clearTimeout(closeTimer.current);
        patch({ open: false, active: -1 });
    }, [patch]);

    const onInput = useCallback((value: string) => {
        patch({ query: value, open: true });
        window.clearTimeout(searchTimer.current);
        searchTimer.current = window.setTimeout(() => void run({ query: value }), 220);
    }, [patch, run]);

    const onPointerDown = useCallback(() => {
        if (stateRef.current.open) {
            close();
            return;
        }
        openList();
    }, [close, openList]);

    const onBlur = useCallback(() => {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => patch({ open: false, active: -1 }), 120);
    }, [patch]);

    const move = useCallback((delta: number) => {
        const current = stateRef.current;
        const total = current.results.length;
        if (!total) {
            return;
        }
        if (!current.open) {
            openList();
            return;
        }
        const next = current.active + delta;
        patch({ active: next < 0 ? total - 1 : next % total });
    }, [openList, patch]);

    const activeRecord = useCallback(() => {
        const current = stateRef.current;
        return current.active >= 0 ? current.results[current.active] ?? null : null;
    }, []);

    const showCreate = useCallback((canCreate: boolean) => {
        const current = stateRef.current;
        const term = current.query.trim();
        if (!canCreate || !term || current.loading) {
            return false;
        }
        return !current.results.some((row) => (row.name || "").toLowerCase() === term.toLowerCase());
    }, []);

    const reset = useCallback(() => {
        window.clearTimeout(searchTimer.current);
        window.clearTimeout(closeTimer.current);
        lastKey.current = null;
        setState(INITIAL);
    }, []);

    return {
        state, patch, openList, close, onInput, onPointerDown, onBlur, move, activeRecord,
        showCreate, reset,
    };
}
